/**
 * Translate a browser-driving tool invocation into a single line of plain
 * language for a live activity surface (web chrome rail, extension side
 * panel). Returns null for tools that aren't browser-driving — caller skips
 * updating the surface entirely.
 *
 * Voice rules: present tense, no tool names, no selectors, no coordinates.
 * We narrate what the user would SEE the agent doing, not the API call —
 * how it's being done.
 *
 * Tool name shapes we accept:
 *   computer_screenshot              (rare — bare)
 *   computer_click                   (rare — bare)
 *   mcp__browser-control__computer_screenshot   (current — MCP-wrapped)
 *   mcp__browser-control__computer_click        (current — MCP-wrapped)
 *   computer                                    (Anthropic unified, action in input)
 * The MCP server is named `browser-control` (legacy); the tools inside it
 * are all `computer_*`. So the discriminator is "ends with computer_<verb>"
 * regardless of MCP wrapping, with the unified `computer` tool falling
 * back to `input.action` for the verb.
 *
 * Moved verbatim from apps/web streamingHandlers so every conversation
 * surface narrates browser activity with one voice.
 */
export function describeBrowserAction(toolName: string, toolInput: unknown): string | null {
  const input = (toolInput && typeof toolInput === 'object')
    ? (toolInput as Record<string, unknown>)
    : {};

  // Strip MCP wrapper to get the bare tool function name. Production
  // shape is `mcp__browser-control__computer_<verb>`; the non-greedy
  // pattern handles any future MCP server name (with hyphens, dots,
  // or underscores).
  const bare = toolName.replace(/^mcp__.+?__/, '');

  // Three matching shapes:
  //   1. bare === 'computer'           → Anthropic unified tool, kind from input.action
  //   2. bare.startsWith('computer_')  → split tool, kind from suffix
  //   3. anything else                 → not browser-driving
  const isUnified = bare === 'computer';
  const isSplit = bare.startsWith('computer_');
  if (!isUnified && !isSplit) return null;

  const action = typeof input.action === 'string' ? input.action : '';
  const kind = isUnified ? action : bare.replace(/^computer_/, '');

  switch (kind) {
    case 'navigate':
    case 'goto': {
      const url = typeof input.url === 'string' ? input.url : '';
      const friendly = friendlyDestination(url);
      return friendly ? `Heading to ${friendly}` : 'Opening a page';
    }
    case 'click':
    case 'left_click':
    case 'double_click':
    case 'middle_click': {
      const label = typeof input.text === 'string' ? input.text.trim() : '';
      return label ? `Clicking "${truncate(label, 40)}"` : 'Tapping the page';
    }
    case 'right_click':
      return 'Right-clicking the page';
    case 'type':
    case 'key_type': {
      const text = typeof input.text === 'string' ? input.text : '';
      return describeTyping(text);
    }
    case 'key':
    case 'key_press': {
      const keys = typeof input.text === 'string'
        ? input.text
        : Array.isArray(input.keys)
          ? (input.keys as unknown[]).filter((k): k is string => typeof k === 'string').join('+')
          : '';
      return keys ? `Pressing ${formatKeys(keys)}` : 'Pressing a key';
    }
    case 'scroll': {
      const dir = typeof input.scroll_direction === 'string'
        ? input.scroll_direction
        : typeof input.direction === 'string'
          ? input.direction
          : '';
      return dir ? `Scrolling ${dir} the page` : 'Scrolling the page';
    }
    case 'screenshot':
    case 'cursor_position':
      return 'Looking at the page';
    case 'wait':
      return 'Waiting for the page to load';
    case 'mouse_move':
    case 'left_mouse_down':
    case 'left_mouse_up':
    case 'left_click_drag':
      return 'Moving on the page';
    default:
      // Unknown computer_* sub-action — fall back to a soft generic line
      // rather than leaking the technical name into the UI.
      return 'Working on the page';
  }
}

function friendlyDestination(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    const host = u.host.replace(/^www\./, '');
    // Friendly-name common destinations the user is likely to recognize.
    const friendlyByHost: Record<string, string> = {
      'gmail.com': 'Gmail',
      'mail.google.com': 'Gmail',
      'calendar.google.com': 'Google Calendar',
      'docs.google.com': 'Google Docs',
      'drive.google.com': 'Google Drive',
      'github.com': 'GitHub',
      'linkedin.com': 'LinkedIn',
      'twitter.com': 'Twitter',
      'x.com': 'X',
      'news.ycombinator.com': 'Hacker News',
      'reddit.com': 'Reddit',
      'youtube.com': 'YouTube',
      'stackoverflow.com': 'Stack Overflow',
      'notion.so': 'Notion',
      'figma.com': 'Figma',
      'stripe.com': 'Stripe',
      'dashboard.stripe.com': 'Stripe',
    };
    return friendlyByHost[host] ?? host;
  } catch {
    return '';
  }
}

function describeTyping(text: string): string {
  const t = text.trim();
  if (!t) return 'Typing';
  // Recognise common shapes so the rail says something humans understand
  // instead of leaking the literal value into the UI.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return 'Filling in your email';
  if (t.length >= 6 && /[A-Za-z]/.test(t) && /\d/.test(t) && !/\s/.test(t)) {
    return 'Entering a password';
  }
  if (/^https?:\/\//i.test(t)) return 'Entering a link';
  return `Typing "${truncate(t, 30)}"`;
}

function formatKeys(keys: string): string {
  // Normalise common synonyms so the rail looks tidy.
  return keys
    .split(/[+\s]+/)
    .filter(Boolean)
    .map((k) => {
      const lower = k.toLowerCase();
      if (lower === 'return' || lower === 'enter') return 'Enter';
      if (lower === 'esc' || lower === 'escape') return 'Esc';
      if (lower === 'cmd' || lower === 'meta') return '⌘';
      if (lower === 'ctrl' || lower === 'control') return 'Ctrl';
      if (lower === 'alt' || lower === 'option') return 'Alt';
      if (lower === 'shift') return 'Shift';
      if (lower === 'tab') return 'Tab';
      if (lower === 'space') return 'Space';
      return k.length === 1 ? k.toUpperCase() : k;
    })
    .join('+');
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}
